import type { CollapseProps } from 'antd'
import { Collapse, Empty, Modal, Skeleton, Tag } from 'antd'
import { useRolodexEdit, useRolodexIds } from 'features/fde'
import {
  DocumentList,
  FileUploader,
  RolodexDocumentEntry,
} from 'features/files'
import { useGeckoNotifications } from 'features/notifications'
import { useRolodexEntries } from 'features/rolodex'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryType } from 'types'

import { inferCategoryLabel } from './fieldEditor/CategorySelector'

interface DocumentUploadListProps {
  isLoading: boolean
  forceRefresh: () => Promise<void>
  documents: ReadonlyArray<RolodexDocumentEntry>
  selectedDocumentId: string | null
  setSelectedDocumentId: Dispatch<SetStateAction<string | null>>
}

const DocumentCategoryTags: FC<{ categories: ReadonlyArray<string> }> = ({
  categories,
}) => {
  const sortedCategories = useMemo(() => {
    return sortBy(categories)
  }, [categories])
  return (
    <div>
      {sortedCategories.map((category) => (
        <Tag key={category} color='blue'>
          {inferCategoryLabel(category)}
        </Tag>
      ))}
    </div>
  )
}

export const DocumentUploadList: FC<DocumentUploadListProps> = ({
  isLoading: paramIsLoading,
  documents,
  selectedDocumentId,
  setSelectedDocumentId,
  forceRefresh,
}) => {
  const unitIds = useMemo(
    () =>
      documents
        .map((d) => d.tags.unit)
        .filter((id): id is string => id !== undefined),
    [documents]
  )
  const { entries: unitEntries, isLoading: isLoadingUnits } = useRolodexEntries(
    useMemo(
      () => ({
        type: [RolodexEntryType.UNIT],
        ids: unitIds,
      }),
      [unitIds]
    )
  )

  const unitNames = useMemo<ReadonlyMap<string, string>>(
    () =>
      new Map(
        unitEntries.map((e) => [
          e.id,
          (e.data.name as string) ?? 'Untitled Unit',
        ])
      ),
    [unitEntries]
  )

  const unitEntryMap = useMemo<ReadonlyMap<string, GeckoEntry>>(
    () => new Map(unitEntries.map((e) => [e.id, e])),
    [unitEntries]
  )

  const unitIdToDocuments = useMemo(
    () =>
      groupBy(
        documents.filter((d) => d.tags.unit),
        (d) => d.tags.unit
      ),
    [documents]
  )

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const documentToDelete = useMemo(
    () => documents.find((d) => d.entry_id === deleteConfirmId),
    [documents, deleteConfirmId]
  )

  const { errorNotification, successNotification } = useGeckoNotifications()

  const [isDeleting, setIsDeleting] = useState(false)
  const { rolodexDelete } = useRolodexEdit()
  const handleDelete = useCallback(
    async (ids: ReadonlyArray<string>) => {
      setIsDeleting(true)
      try {
        await Promise.all(ids.map((id) => rolodexDelete(id, true)))
        successNotification('Delete Successful')
        await forceRefresh()
      } catch (error) {
        errorNotification(`Delete Failed: ${String(error)}`)
      } finally {
        setIsDeleting(false)
        setDeleteConfirmId(null)
      }
    },
    [errorNotification, forceRefresh, rolodexDelete, successNotification]
  )

  const collapseItems = useMemo<CollapseProps['items']>(() => {
    return Object.entries(unitIdToDocuments).map(([unitId, unitDocuments]) => {
      const displayUnitName = unitNames.get(unitId) ?? 'Untitled Unit'

      return {
        key: unitId,
        label: displayUnitName,
        children: (
          <PanelContent>
            <DocumentList
              documents={unitDocuments}
              selectedDocumentId={selectedDocumentId}
              onDocumentSelect={setSelectedDocumentId}
              size='small'
              onDelete={async (documentId) => {
                setDeleteConfirmId(documentId)
              }}
              afterItem={(doc) => (
                <DocumentCategoryTags categories={doc.categories} />
              )}
            />
            {unitEntryMap.get(unitId) && (
              <FileUploader
                onUploadComplete={async () => {
                  successNotification('Upload Successful')
                  await forceRefresh()
                }}
                onUploadError={() => errorNotification('Upload Failed')}
                tags={{
                  ...(unitEntryMap.get(unitId)?.tags ?? {}),
                  unit: unitId,
                }}
                entry={unitEntryMap.get(unitId)!}
                showExistingFiles={false}
                condensed
              />
            )}
          </PanelContent>
        ),
      }
    })
  }, [
    errorNotification,
    forceRefresh,
    selectedDocumentId,
    setSelectedDocumentId,
    successNotification,
    unitEntryMap,
    unitIdToDocuments,
    unitNames,
  ])

  const [activeKey, setActiveKey] = useState<ReadonlyArray<string>>([])
  useEffect(() => {
    if (unitEntries.length === 1) {
      setActiveKey([unitEntries[0].id])
    }
  }, [unitEntries])

  const isLoading = paramIsLoading || isLoadingUnits || isDeleting
  return isLoading ? (
    <Skeleton active paragraph={{ rows: 4 }} />
  ) : !documents.length ? (
    <Empty description='No document uploads found' />
  ) : (
    <>
      <DeleteConfirmModal
        documentId={deleteConfirmId}
        documentFilename={documentToDelete?.filename ?? null}
        documentType={documentToDelete?.type ?? null}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteConfirmId(null)
        }}
      />
      <StyledCollapse
        items={collapseItems}
        bordered={false}
        activeKey={[...activeKey]}
        onChange={setActiveKey}
      />
    </>
  )
}

const DeleteConfirmModal: React.FC<{
  documentId: string | null
  documentFilename: string | null
  documentType: RolodexEntryType | null
  onOk: (ids: ReadonlyArray<string>) => Promise<void>
  onCancel: (ids: ReadonlyArray<string>) => void
}> = ({ documentId, documentType, documentFilename, onOk, onCancel }) => {
  const { ids, isLoading } = useRolodexIds(
    documentId && documentType
      ? {
          tags: { [documentType]: documentId },
        }
      : undefined
  )

  const allIds = useMemo(
    () =>
      new Set(
        [documentId, ...(ids ?? [])].filter((id): id is string => id !== null)
      ),
    [documentId, ids]
  )

  const linkedCount = allIds.size - 1

  const [isDeleting, setIsDeleting] = useState(false)
  const handleDelete = useCallback(async () => {
    setIsDeleting(true)
    try {
      await onOk(Array.from(allIds))
    } finally {
      setIsDeleting(false)
    }
  }, [onOk, allIds])

  return (
    <Modal
      open={!!documentId}
      okButtonProps={{ disabled: isLoading, loading: isDeleting }}
      cancelButtonProps={{ disabled: isLoading || isDeleting }}
      onOk={async () => {
        await handleDelete()
      }}
      onCancel={() => {
        onCancel(Array.from(allIds))
      }}>
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : (
        <p>
          Are you sure you want to delete document &apos;{documentFilename}
          &apos; and {linkedCount} linked entries?
        </p>
      )}
    </Modal>
  )
}

const StyledCollapse = styled(Collapse)`
  width: 100%;

  .ant-collapse-item {
    width: 100%;
  }

  .ant-collapse-header {
    width: 100%;
  }
`

const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`
